// src/api/axios.js
import axios from 'axios';
import {
  shouldUseMockAuth,
  mockRefreshToken
} from '../utils/mockAuth';
import { routeMockRequest, isMockEnabled } from '../mocks';

const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:8000/';

const api = axios.create({
  baseURL: BASE_URL,
});

// ============================================================
// TOKEN REFRESH STATE MANAGEMENT
// Prevents multiple parallel refresh attempts
// ============================================================
let isRefreshing = false;
let failedQueue = [];

// ============================================================
// AUTH EXPIRED CIRCUIT BREAKER
// Once tripped, all outgoing requests are short-circuited so
// pollers stop hammering the backend with 401s during redirect.
// ============================================================
let authExpired = false;

export const resetAuthExpired = () => {
  authExpired = false;
};

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const clearAuthAndRedirect = () => {
  // Trip the breaker BEFORE touching storage / redirecting so that any
  // request fired by a still-running poller in the same tick is rejected
  // immediately instead of producing another 401.
  authExpired = true;
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  // Avoid scheduling multiple navigations if several 401s race here.
  if (window.location.pathname !== '/login') {
    window.location.replace('/login');
  }
};

// Check if request is a token refresh request
const isRefreshRequest = (url) => {
  return url && url.includes('api/token/refresh/');
};

// Check if request is a login request
const isLoginRequest = (url) => {
  return url && url.includes('api/token/') && !url.includes('refresh');
};

// ============================================================
// REQUEST INTERCEPTOR
// ============================================================
api.interceptors.request.use(async (config) => {
  // Circuit breaker: once auth has expired, drop every outgoing request
  // until the page actually navigates to /login. Login attempts are
  // allowed through so the user can sign back in.
  // Use a plain Error (not axios.Cancel) so callers' .catch handlers
  // run normally and any `loading` state gets released.
  if (authExpired && !isLoginRequest(config.url)) {
    const err = new Error('Auth expired - request blocked');
    err.config = config;
    err.isAuthExpired = true;
    return Promise.reject(err);
  }

  // Add JWT token to all requests (except login)
  if (!isLoginRequest(config.url)) {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }

  // ============================================================
  // CENTRALIZED MOCK ROUTING
  // Only active when REACT_APP_USE_MOCKS=true
  // ============================================================
  if (isMockEnabled()) {
    try {
      const mockResponse = await routeMockRequest(
        config.method?.toUpperCase() || 'GET',
        config.url || '',
        config.data,
        config.params
      );

      // If mock handler found, intercept request and return mock data
      if (mockResponse !== null) {
        console.log('🔧 [MOCK] Using mock data for:', config.method, config.url);

        config.adapter = () => {
          return Promise.resolve({
            data: mockResponse.data,
            status: 200,
            statusText: 'OK (Mock)',
            headers: {},
            config: config,
          });
        };
      }
    } catch (mockError) {
      // Mock handler threw an error (e.g., 404, 400)
      console.log('🔧 [MOCK] Mock error for:', config.method, config.url, mockError);

      config.adapter = () => {
        return Promise.reject(mockError);
      };
    }
  }

  return config;
});

// ============================================================
// RESPONSE INTERCEPTOR
// ============================================================
api.interceptors.response.use(
  (response) => {
    // Any successful authenticated response means the session is healthy:
    // release the circuit breaker so future polls can resume.
    if (response?.config?.url && !isRefreshRequest(response.config.url)) {
      authExpired = false;
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // No config means something went very wrong - just reject
    if (!originalRequest) {
      return Promise.reject(error);
    }

    const useMock = shouldUseMockAuth();

    // ============================================================
    // CRITICAL: Never retry refresh token requests
    // If refresh fails with 401, stop immediately and redirect to login
    // ============================================================
    if (isRefreshRequest(originalRequest.url)) {
      if (error.response && error.response.status === 401) {
        console.error('[AUTH] Refresh token invalid or expired - redirecting to login');
        isRefreshing = false;
        processQueue(error, null);
        clearAuthAndRedirect();
        return Promise.reject(error);
      }
      // For other errors on refresh, just reject (don't redirect)
      return Promise.reject(error);
    }

    // A 401 from the login endpoint is just "wrong credentials" — don't
    // trip the circuit breaker, let the login form surface the error.
    if (isLoginRequest(originalRequest.url)) {
      return Promise.reject(error);
    }

    // ============================================================
    // Handle 401 errors (except for refresh requests handled above)
    // ============================================================
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      // Check if we have a refresh token before attempting refresh
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        console.error('[AUTH] No refresh token available - redirecting to login');
        clearAuthAndRedirect();
        return Promise.reject(error);
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        let newAccessToken;

        // Try real backend first (if not in mock mode)
        if (!useMock) {
          try {
            // CRITICAL: Use raw axios.post() to bypass interceptor
            // This prevents infinite loop when refresh itself returns 401
            const response = await axios.post(`${BASE_URL}api/token/refresh/`, {
              refresh: refreshToken
            });
            newAccessToken = response.data.access;
          } catch (refreshError) {
            // Real refresh failed - throw to outer catch
            throw refreshError;
          }
        } else {
          // Mock mode - use mock refresh
          const mockResponse = await mockRefreshToken(refreshToken);
          newAccessToken = mockResponse.data.access;
        }

        // Success - save new token and process queue
        localStorage.setItem('accessToken', newAccessToken);
        isRefreshing = false;
        processQueue(null, newAccessToken);

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);

      } catch (refreshError) {
        // Refresh failed completely
        console.error('[AUTH] Token refresh failed:', refreshError);
        isRefreshing = false;
        processQueue(refreshError, null);
        clearAuthAndRedirect();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;